// RichTextToolbar.tsx
// リッチテキスト用ツールバーコンポーネント

import React, { useState, useRef } from "react";
import {
  Box,
  Divider,
  Select,
  MenuItem,
  Popover,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  SelectChangeEvent,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Code,
  Link as LinkIcon,
  TableChart,
  FormatIndentIncrease,
  FormatIndentDecrease,
  HorizontalRule,
  FormatClear,
  MoreHoriz,
  Undo,
  Redo,
  FormatColorText,
  FormatColorFill,
  Image as ImageIcon,
  Add,
  Delete,
  DeveloperMode,
} from "@mui/icons-material";
import type { Editor } from "@tiptap/react";
import { Extension } from "@tiptap/core";

// ────────── カスタム拡張の型宣言 ──────────

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
    indent: {
      increaseIndent: () => ReturnType;
      decreaseIndent: () => ReturnType;
    };
  }
}

// ────────── カスタム拡張：フォントサイズ ──────────

export const FontSize = Extension.create({
  name: "fontSize",

  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) =>
              element.style.fontSize?.replace(/['"]+/g, "") || null,
            renderHTML: (attributes: Record<string, any>) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }: any) => {
          return chain().setMark("textStyle", { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }: any) => {
          return chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

// ────────── カスタム拡張：インデント ──────────

export const Indent = Extension.create({
  name: "indent",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading"],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element: HTMLElement) => {
              const indent = element.getAttribute("data-indent");
              return indent ? parseInt(indent, 10) : 0;
            },
            renderHTML: (attributes: Record<string, any>) => {
              if (!attributes.indent || attributes.indent === 0) return {};
              return {
                "data-indent": attributes.indent,
                style: `margin-left: ${attributes.indent * 2}em`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      increaseIndent:
        () =>
        ({ tr, state, dispatch }: any) => {
          const { from, to } = state.selection;
          let changed = false;
          state.doc.nodesBetween(from, to, (node: any, pos: number) => {
            if (["paragraph", "heading"].includes(node.type.name)) {
              const indent = (node.attrs.indent || 0) + 1;
              if (indent <= 10) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent,
                });
                changed = true;
              }
            }
          });
          if (changed && dispatch) dispatch(tr);
          return changed;
        },
      decreaseIndent:
        () =>
        ({ tr, state, dispatch }: any) => {
          const { from, to } = state.selection;
          let changed = false;
          state.doc.nodesBetween(from, to, (node: any, pos: number) => {
            if (["paragraph", "heading"].includes(node.type.name)) {
              const indent = Math.max((node.attrs.indent || 0) - 1, 0);
              if (indent !== (node.attrs.indent || 0)) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent,
                });
                changed = true;
              }
            }
          });
          if (changed && dispatch) dispatch(tr);
          return changed;
        },
    };
  },
});

// ────────── ユーティリティ ──────────

/** URLにプロトコルが無い場合は https:// を補完する */
function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

// ────────── 定数 ──────────

// フォントサイズ選択肢
const FONT_SIZES = [
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
  "36px",
  "48px",
  "72px",
];

// テキストカラーパレット
const TEXT_COLORS = [
  "#000000", "#434343", "#666666", "#999999", "#B7B7B7", "#CCCCCC", "#EFEFEF", "#FFFFFF",
  "#FF0000", "#FF6600", "#FFD700", "#008000", "#0099FF", "#0000FF", "#9933FF", "#FF00FF",
  "#CC0000", "#CC5200", "#B8860B", "#006600", "#006699", "#000099", "#660099", "#990066",
  "#FF9999", "#FFB366", "#FFEB99", "#99FF99", "#99CCFF", "#9999FF", "#CC99FF", "#FF99CC",
];

// ハイライトカラーパレット
const HIGHLIGHT_COLORS = [
  "#FFFF00", "#00FF00", "#00FFFF", "#FF00FF", "#FF0000", "#0000FF",
  "#FFC0CB", "#FFA500", "#ADD8E6", "#90EE90", "#DDA0DD", "#FFD700",
  "#E6E6FA", "#F0E68C", "#98FB98", "#AFEEEE",
];

// ────────── サブコンポーネント ──────────

// カラーピッカーボタン
function ColorPickerButton({
  icon,
  tooltip,
  colors,
  currentColor,
  onSelect,
  onClear,
}: {
  icon: React.ReactNode;
  tooltip: string;
  colors: string[];
  currentColor?: string;
  onSelect: (color: string) => void;
  onClear: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            borderRadius: 1,
            color: currentColor || "inherit",
          }}
        >
          {icon}
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(8, 24px)",
              gap: 0.5,
            }}
          >
            {colors.map((color) => (
              <Box
                key={color}
                onClick={() => {
                  onSelect(color);
                  setAnchorEl(null);
                }}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: color,
                  border: "1px solid #ccc",
                  borderRadius: 0.5,
                  cursor: "pointer",
                  "&:hover": { opacity: 0.8, transform: "scale(1.1)" },
                }}
              />
            ))}
          </Box>
          <Button
            size="small"
            onClick={() => {
              onClear();
              setAnchorEl(null);
            }}
          >
            クリア
          </Button>
        </Box>
      </Popover>
    </>
  );
}

// リンクダイアログ
function LinkDialog({
  open,
  defaultUrl,
  onClose,
  onConfirm,
  onRemove,
}: {
  open: boolean;
  defaultUrl: string;
  onClose: () => void;
  onConfirm: (url: string) => void;
  onRemove: () => void;
}) {
  const [url, setUrl] = useState(defaultUrl);

  React.useEffect(() => {
    if (open) setUrl(defaultUrl);
  }, [open, defaultUrl]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>リンクの挿入</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        {defaultUrl && (
          <Button
            color="error"
            onClick={() => {
              onRemove();
              onClose();
            }}
          >
            リンクを解除
          </Button>
        )}
        <Button onClick={onClose}>キャンセル</Button>
        <Button
          variant="contained"
          onClick={() => {
            onConfirm(url);
            onClose();
          }}
          disabled={!url.trim()}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ────────── メインツールバー ──────────

type RichTextToolbarProps = {
  editor: Editor | null;
};

export function RichTextToolbar({ editor }: RichTextToolbarProps) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [tableMenuAnchor, setTableMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  // エディタの選択範囲を常に追跡する
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);

  // selectionUpdate イベントで選択範囲を常時保存する
  React.useEffect(() => {
    if (!editor) return;
    const handleSelectionUpdate = () => {
      const { from, to } = editor.state.selection;
      savedSelectionRef.current = { from, to };
    };
    // 初期値を保存
    handleSelectionUpdate();
    editor.on("selectionUpdate", handleSelectionUpdate);
    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate);
    };
  }, [editor]);

  if (!editor) return null;

  // 現在のフォントサイズを取得
  const currentFontSize =
    editor.getAttributes("textStyle")?.fontSize || "";

  // 現在のテキストカラーを取得
  const currentTextColor =
    editor.getAttributes("textStyle")?.color || "";

  // 現在のリンクURLを取得
  const currentLinkUrl = editor.getAttributes("link")?.href || "";

  // 現在のブロックタイプを取得
  const getCurrentBlockType = (): string => {
    if (editor.isActive("heading", { level: 1 })) return "h1";
    if (editor.isActive("heading", { level: 2 })) return "h2";
    if (editor.isActive("heading", { level: 3 })) return "h3";
    return "paragraph";
  };

  // 画像挿入ハンドラ
  const handleImageInsert = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      editor.chain().focus().setImage({ src: base64 }).run();
    };
    reader.readAsDataURL(file);
  };

  // ツールバーボタンの共通スタイル
  const btnSx = {
    borderRadius: 1,
    minWidth: 32,
    p: 0.5,
  };

  // アクティブ状態のボタンスタイル
  const activeBtnSx = (isActive: boolean) => ({
    ...btnSx,
    bgcolor: isActive ? "action.selected" : "transparent",
    color: isActive ? "primary.main" : "inherit",
  });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.25,
          alignItems: "center",
          p: 0.5,
          bgcolor: "#fafafa",
          border: 1,
          borderColor: "rgba(0, 0, 0, 0.23)",
          borderRadius: "4px 4px 0 0",
        }}
      >
        {/* 元に戻す/やり直す */}
        <Tooltip title="元に戻す">
          <span>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              sx={btnSx}
            >
              <Undo fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="やり直す">
          <span>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              sx={btnSx}
            >
              <Redo fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        {/* 段落/見出し */}
        <Select
          size="small"
          value={getCurrentBlockType()}
          onChange={(e: SelectChangeEvent) => {
            const val = e.target.value;
            if (val === "paragraph") {
              editor.chain().focus().setParagraph().run();
            } else {
              const level = parseInt(val.replace("h", "")) as 1 | 2 | 3;
              editor.chain().focus().toggleHeading({ level }).run();
            }
          }}
          sx={{ minWidth: 120, height: 32, fontSize: "0.8rem" }}
        >
          <MenuItem value="paragraph">標準テキスト</MenuItem>
          <MenuItem value="h1">見出し 1</MenuItem>
          <MenuItem value="h2">見出し 2</MenuItem>
          <MenuItem value="h3">見出し 3</MenuItem>
        </Select>

        {/* フォントサイズ */}
        <Select
          size="small"
          displayEmpty
          value={currentFontSize}
          onChange={(e: SelectChangeEvent) => {
            const size = e.target.value;
            if (size) {
              editor.chain().focus().setFontSize(size).run();
            } else {
              editor.chain().focus().unsetFontSize().run();
            }
          }}
          sx={{ minWidth: 72, height: 32, fontSize: "0.8rem", ml: 0.5 }}
          renderValue={(val) => (val as string) || "サイズ"}
        >
          <MenuItem value="">デフォルト</MenuItem>
          {FONT_SIZES.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        {/* 文字装飾 */}
        <ToggleButtonGroup size="small">
          <ToggleButton
            value="bold"
            selected={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
            sx={btnSx}
          >
            <Tooltip title="太字">
              <FormatBold fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="italic"
            selected={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            sx={btnSx}
          >
            <Tooltip title="斜体">
              <FormatItalic fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="underline"
            selected={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            sx={btnSx}
          >
            <Tooltip title="下線">
              <FormatUnderlined fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="strike"
            selected={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            sx={btnSx}
          >
            <Tooltip title="取り消し線">
              <StrikethroughS fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        {/* テキストカラー */}
        <ColorPickerButton
          icon={<FormatColorText fontSize="small" />}
          tooltip="フォントの色"
          colors={TEXT_COLORS}
          currentColor={currentTextColor}
          onSelect={(color) => editor.chain().focus().setColor(color).run()}
          onClear={() => editor.chain().focus().unsetColor().run()}
        />

        {/* ハイライト */}
        <ColorPickerButton
          icon={<FormatColorFill fontSize="small" />}
          tooltip="テキストハイライト"
          colors={HIGHLIGHT_COLORS}
          onSelect={(color) =>
            editor.chain().focus().toggleHighlight({ color }).run()
          }
          onClear={() => editor.chain().focus().unsetHighlight().run()}
        />

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        {/* リスト */}
        <ToggleButtonGroup size="small">
          <ToggleButton
            value="bulletList"
            selected={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            sx={btnSx}
          >
            <Tooltip title="箇条書き">
              <FormatListBulleted fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="orderedList"
            selected={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            sx={btnSx}
          >
            <Tooltip title="番号付きリスト">
              <FormatListNumbered fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        {/* リンク */}
        <Tooltip title="リンク">
          <IconButton
            size="small"
            onClick={() => {
              const sel = savedSelectionRef.current;
              if (sel && sel.from !== sel.to) {
                // テキスト選択あり → 選択テキストをURLとして直接リンク化
                const selectedText = editor.state.doc.textBetween(sel.from, sel.to);
                editor
                  .chain()
                  .focus()
                  .setTextSelection(sel)
                  .setLink({ href: normalizeUrl(selectedText) })
                  .run();
              } else {
                // テキスト未選択 → ダイアログを開いてURL入力
                setLinkDialogOpen(true);
              }
            }}
            sx={activeBtnSx(editor.isActive("link"))}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* 画像 */}
        <Tooltip title="画像を挿入">
          <IconButton
            size="small"
            onClick={() => imageInputRef.current?.click()}
            sx={btnSx}
          >
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImageInsert(file);
            e.target.value = "";
          }}
        />

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        {/* 書式クリア */}
        <Tooltip title="すべての書式をクリア">
          <IconButton
            size="small"
            onClick={() =>
              editor.chain().focus().unsetAllMarks().clearNodes().run()
            }
            sx={btnSx}
          >
            <FormatClear fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        {/* その他のツール（折りたたみ） */}
        <Tooltip title="その他のツール">
          <IconButton
            size="small"
            onClick={(e) => setMoreAnchorEl(e.currentTarget)}
            sx={btnSx}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(moreAnchorEl)}
          anchorEl={moreAnchorEl}
          onClose={() => setMoreAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.25,
              alignItems: "center",
              p: 0.5,
              bgcolor: "#fafafa",
            }}
          >
            {/* インデント */}
            <Tooltip title="インデントを減らす">
              <span>
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().decreaseIndent().run()}
                  sx={btnSx}
                >
                  <FormatIndentDecrease fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="インデントを増やす">
              <span>
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().increaseIndent().run()}
                  sx={btnSx}
                >
                  <FormatIndentIncrease fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

            {/* 引用・コード */}
            <ToggleButtonGroup size="small">
              <ToggleButton
                value="blockquote"
                selected={editor.isActive("blockquote")}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                sx={btnSx}
              >
                <Tooltip title="引用">
                  <FormatQuote fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                value="code"
                selected={editor.isActive("code")}
                onClick={() => editor.chain().focus().toggleCode().run()}
                sx={btnSx}
              >
                <Tooltip title="コード">
                  <Code fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                value="codeBlock"
                selected={editor.isActive("codeBlock")}
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                sx={btnSx}
              >
                <Tooltip title="コードブロック">
                  <DeveloperMode fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

            {/* 水平線 */}
            <Tooltip title="水平線を挿入">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                sx={btnSx}
              >
                <HorizontalRule fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* テーブル */}
            <Tooltip title="テーブル">
              <IconButton
                size="small"
                onClick={(e) => {
                  setMoreAnchorEl(null);
                  setTableMenuAnchor(e.currentTarget);
                }}
                sx={activeBtnSx(editor.isActive("table"))}
              >
                <TableChart fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Popover>
      </Box>

      {/* リンクダイアログ */}
      <LinkDialog
        open={linkDialogOpen}
        defaultUrl={currentLinkUrl}
        onClose={() => setLinkDialogOpen(false)}
        onConfirm={(url) => {
          const sel = savedSelectionRef.current;
          if (!sel) return;

          if (sel.from === sel.to) {
            // テキスト未選択 → URLをリンクテキストとして挿入
            const normalized = normalizeUrl(url);
            editor
              .chain()
              .focus()
              .setTextSelection(sel)
              .insertContent({
                type: "text",
                text: url,
                marks: [{ type: "link", attrs: { href: normalized } }],
              })
              .run();
          } else {
            // テキスト選択あり → 選択範囲にリンクを適用
            editor
              .chain()
              .focus()
              .setTextSelection(sel)
              .setLink({ href: normalizeUrl(url) })
              .run();
          }
        }}
        onRemove={() => {
          const sel = savedSelectionRef.current;
          if (sel) {
            editor
              .chain()
              .focus()
              .setTextSelection(sel)
              .extendMarkRange("link")
              .unsetLink()
              .run();
          }
        }}
      />

      {/* テーブル操作メニュー */}
      <Menu
        anchorEl={tableMenuAnchor}
        open={Boolean(tableMenuAnchor)}
        onClose={() => setTableMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run();
            setTableMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <TableChart fontSize="small" />
          </ListItemIcon>
          <ListItemText>テーブルを挿入</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            editor.chain().focus().addRowAfter().run();
            setTableMenuAnchor(null);
          }}
          disabled={!editor.can().addRowAfter()}
        >
          <ListItemIcon>
            <Add fontSize="small" />
          </ListItemIcon>
          <ListItemText>行を追加</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().addColumnAfter().run();
            setTableMenuAnchor(null);
          }}
          disabled={!editor.can().addColumnAfter()}
        >
          <ListItemIcon>
            <Add fontSize="small" />
          </ListItemIcon>
          <ListItemText>列を追加</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            editor.chain().focus().deleteRow().run();
            setTableMenuAnchor(null);
          }}
          disabled={!editor.can().deleteRow()}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>行を削除</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().deleteColumn().run();
            setTableMenuAnchor(null);
          }}
          disabled={!editor.can().deleteColumn()}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>列を削除</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().deleteTable().run();
            setTableMenuAnchor(null);
          }}
          disabled={!editor.can().deleteTable()}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>テーブルを削除</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default RichTextToolbar;
